import { FC, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command.tsx'
import { Tag } from '@/js/editor/components/general/Tag.tsx'
import { Filter } from 'lucide-react'
import { useTags } from '@/js/editor/hooks/api/useTags.ts'
import { Tag as TagType } from '@/js/types'
import { useAtomValue } from 'jotai'
import { tagsAtom } from '@/js/editor/state/mapAtoms.ts'
import { toast } from 'sonner'

type TagFilterProps = {
    activeTags: Array<TagType>
    filter: (tag: TagType) => void
}

export const TagFilter: FC<TagFilterProps> = ({ activeTags, filter }) => {
    const tags = useAtomValue(tagsAtom)
    const [isOpen, setIsOpen] = useState(false)
    const [value, setValue] = useState('')
    const { create } = useTags()

    if (!tags) {
        return null
    }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <div
                    className={`cursor-pointer flex shrink-0 -mr-2 justify-center items-center hover:bg-mm-bg3 w-[32px] h-[32px] rounded-md transition-color hover:ring ring-slate-300 ${activeTags.length > 0 || isOpen ? 'bg-mm-bg3 ring' : ''}`}
                >
                    <Filter
                        size={18}
                        className={`shrink-0`}
                        color={'var(--color-mm-gray)'}
                        onClick={() => setIsOpen(true)}
                    />
                </div>
            </PopoverTrigger>
            <PopoverContent className={'z-2005 p-0 w-50'}>
                <Command>
                    <CommandInput placeholder={'Suchen oder erstellen'} value={value} onValueChange={setValue} />
                    <CommandList>
                        <CommandEmpty>Noch keine Kategorien vorhanden</CommandEmpty>
                        <CommandGroup>
                            {tags?.map((tag) => {
                                // don't render tag when its already assigned
                                if (activeTags.some((activeTag) => activeTag.id === tag.id)) {
                                    return null
                                }

                                return (
                                    <CommandItem
                                        key={tag.name}
                                        value={tag.name}
                                        className={'flex items-center justify-between'}
                                        onSelect={() => {
                                            filter(tag)
                                        }}
                                    >
                                        <Tag title={tag.name} />
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                        {value.trim() && (
                            <CommandGroup heading={'Neue Kategorie'}>
                                <CommandItem
                                    value={value}
                                    onSelect={async () => {
                                        try {
                                            const newTag = await create({
                                                name: value.trim(),
                                            })

                                            filter(newTag)
                                            setValue('')
                                            setIsOpen(false)
                                        } catch (error) {
                                            toast.error('Kategorie konnte nicht erstellt werden.', {
                                                description:
                                                    error instanceof Error
                                                        ? error.message
                                                        : 'Bitte versuche es erneut.',
                                            })
                                        }
                                    }}
                                >
                                    <Tag title={value.trim()} icon={'plus'} />
                                </CommandItem>
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
